"use client";

import { ReactNode, Suspense, useEffect, useRef, useState } from "react";

/**
 * Defers mounting (and therefore downloading + parsing + executing) of a below-the-fold
 * subtree until it is about to scroll into view. Combined with React.lazy children, the
 * component's webpack chunk is not fetched until needed, cutting the initial home.js bootup.
 *
 * `minHeight` reserves vertical space up front so revealing the real content does not shift
 * the layout (avoids CLS). Falls back to rendering immediately when IntersectionObserver is
 * unavailable (SSR/very old browsers).
 */
export default function LazyOnVisible({
  children,
  minHeight,
  rootMargin = "600px",
  fallback = null,
  className,
}: {
  readonly children: ReactNode;
  readonly minHeight?: number;
  readonly rootMargin?: string;
  readonly fallback?: ReactNode;
  readonly className?: string;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (visible) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [visible, rootMargin]);

  return (
    <div ref={ref} className={className} style={!visible && minHeight != null ? { minHeight } : undefined}>
      {visible ? <Suspense fallback={fallback}>{children}</Suspense> : fallback}
    </div>
  );
}
